import React, { useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Download, FileText, Calendar, CheckCircle, RefreshCw, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';

const OASISExportPage = () => {
  const [exports, setExports] = useState([
    { id: '1', fileName: 'OASIS_Export_20260125.xml', assessments: 45, dateRange: '01/01/2026 - 01/25/2026', status: 'Ready', createdDate: '01/25/2026 03:00 PM' },
    { id: '2', fileName: 'OASIS_Export_20260118.xml', assessments: 38, dateRange: '01/01/2026 - 01/18/2026', status: 'Downloaded', createdDate: '01/18/2026 04:30 PM' },
    { id: '3', fileName: 'OASIS_Export_20260111.xml', assessments: 52, dateRange: '12/15/2025 - 01/11/2026', status: 'Downloaded', createdDate: '01/11/2026 02:15 PM' },
    { id: '4', fileName: 'OASIS_Export_20260104.xml', assessments: 41, dateRange: '12/01/2025 - 01/04/2026', status: 'Downloaded', createdDate: '01/04/2026 11:00 AM' },
  ]);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exportType, setExportType] = useState('all');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = () => {
    if (!dateFrom || !dateTo) {
      toast.error('Please select date range');
      return;
    }
    setIsGenerating(true);
    setTimeout(() => {
      const newExport = {
        id: Date.now().toString(),
        fileName: `OASIS_Export_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xml`,
        assessments: Math.floor(Math.random() * 50) + 20,
        dateRange: `${dateFrom} - ${dateTo}`,
        status: 'Ready',
        createdDate: new Date().toLocaleString()
      };
      setExports(prev => [newExport, ...prev]);
      setIsGenerating(false);
      toast.success('Export file generated successfully');
    }, 2000);
  };

  const handleDownload = (exp) => {
    toast.success(`Downloading ${exp.fileName}...`);
    setExports(prev => prev.map(e => e.id === exp.id ? { ...e, status: 'Downloaded' } : e));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Ready':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Ready</span>;
      case 'Downloaded':
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">Downloaded</span>;
      case 'Processing':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Processing</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="oasis-export-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-[#34495e] rounded-lg flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">OASIS Export</h1>
            <p className="text-gray-600">Generate OASIS assessment export files</p>
          </div>
        </div>

        {/* Generate Export */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Generate New Export</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>From Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div>
              <Label>To Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div>
              <Label>Assessment Type</Label>
              <select
                value={exportType}
                onChange={(e) => setExportType(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="all">All Types</option>
                <option value="soc">Start of Care</option>
                <option value="roc">Resumption of Care</option>
                <option value="recert">Recertification</option>
                <option value="discharge">Discharge</option>
                <option value="transfer">Transfer</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full bg-[#34495e] hover:bg-[#2c3e50]"
              >
                {isGenerating ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Generate Export</>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Export History */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <h3 className="font-semibold text-gray-900">Export History</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">File Name</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Assessments</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date Range</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {exports.map((exp) => (
                  <tr key={exp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{exp.fileName}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center font-semibold text-gray-900">{exp.assessments}</td>
                    <td className="px-4 py-3 text-gray-600">{exp.dateRange}</td>
                    <td className="px-4 py-3 text-center">{getStatusBadge(exp.status)}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{exp.createdDate}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(exp)}
                      >
                        <Download className="w-4 h-4 mr-1" /> Download
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OASISExportPage;
